// The generated SelfServeDataImportSubmissionModel is all-optional (NSwag) — toSubmissionModel
// normalises every response so components need no per-field guards.

import {
  ImportStatus,
  SelfServeDataImportSubmissionModel,
  SubmissionStatus,
  TemplateType,
} from "services/api/cplapi/CPLAPI";

export interface SubmissionModel {
  submissionID: string;
  templateType: TemplateType;
  submissionStatus: SubmissionStatus;
  submissionStatusName?: string;
  importStatus: ImportStatus;
  importStatusName?: string;
  currentStep: number;
  referenceNo?: string;
  // Balance Import only: the parent Static Data submission.
  parentSubmissionID?: string;
  parentReferenceNo?: string;
  createdDate: Date;
  createdUser?: string;
  createdUserName: string;
  updatedDate?: Date;
  updatedUser?: string;
  updatedUserName?: string;
  lockHeldByUserName: string;
  isLockExpired: boolean;
  isLockHeldByCurrentUser: boolean;
}

// Live lock held by someone else. No-lock and expired-lock rows both report isLockExpired true
// (free to take over); rows the current user last locked report isLockHeldByCurrentUser true.
export function isLockedByAnotherUser(item: SubmissionModel): boolean {
  return !item.isLockExpired && !item.isLockHeldByCurrentUser;
}

// Step.Select is the landing page — a normalised SubmissionModel never sits below Upload.
export const Step = {
  Select: 0,
  Upload: 1,
  Parsing: 2,
  Mapping: 3,
  Validation: 4,
  Review: 5,
  Done: 6,
} as const;
export type Step = (typeof Step)[keyof typeof Step];

export const STEP_LABELS: Record<Step, string> = {
  [Step.Select]: "Select",
  [Step.Upload]: "Upload",
  [Step.Parsing]: "Parsing",
  [Step.Mapping]: "Mapping",
  [Step.Validation]: "Validation",
  [Step.Review]: "Review",
  [Step.Done]: "Done",
};

const isStep = (value: number): value is Step => value in STEP_LABELS;

export function getCurrentStepLabel(currentStep: number): string {
  return isStep(currentStep) ? STEP_LABELS[currentStep] : `Step ${currentStep + 1}`;
}

// Past Submitted, importStatus (Tech Ops' outcome) is the meaningful state for display.
export function getSubmissionStatusDisplay(item: SubmissionModel): { label: string; variant: string } {
  if (item.submissionStatus !== SubmissionStatus.Submitted) {
    return { label: item.submissionStatusName ?? item.submissionStatus, variant: "secondary" };
  }

  switch (item.importStatus) {
    case ImportStatus.Complete:
      return { label: "Import Complete", variant: "success" };
    case ImportStatus.InProgress:
      return { label: "Import in Progress", variant: "warning" };
    case ImportStatus.Failed:
      return { label: "Import Failed", variant: "danger" };
    case ImportStatus.Pending:
    default:
      return { label: "Pending Import", variant: "primary" };
  }
}

// The API guarantees these fields on every response — their absence is a malformed payload.
export function toSubmissionModel(generated: SelfServeDataImportSubmissionModel): SubmissionModel {
  const { submissionID, templateType, submissionStatus, importStatus, createdDate } = generated;
  if (!submissionID || !templateType || !submissionStatus || !importStatus || !createdDate) {
    throw new Error("Malformed submission model in API response");
  }

  const clampedWizardStep = Math.min(Math.max(generated.currentStep ?? 0, Step.Upload), Step.Done);
  const currentStep = submissionStatus === SubmissionStatus.Submitted ? Step.Done : clampedWizardStep;

  return {
    submissionID,
    templateType,
    submissionStatus,
    submissionStatusName: generated.submissionStatusName ?? undefined,
    importStatus,
    importStatusName: generated.importStatusName ?? undefined,
    currentStep,
    referenceNo: generated.referenceNo ?? undefined,
    parentSubmissionID: generated.parentSubmissionID ?? undefined,
    parentReferenceNo: generated.parentReferenceNo ?? undefined,
    createdDate,
    createdUser: generated.createdUser,
    createdUserName: generated.createdUserName ?? "",
    updatedDate: generated.updatedDate ?? undefined,
    updatedUser: generated.updatedUser ?? undefined,
    updatedUserName: generated.updatedUserName ?? undefined,
    lockHeldByUserName: generated.lockHeldByUserName ?? "",
    isLockExpired: generated.isLockExpired ?? true,
    isLockHeldByCurrentUser: generated.isLockHeldByCurrentUser ?? false,
  };
}
